import React, { useEffect, useState } from 'react';
import { postApi, getApi } from '../../services/api';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

const Stake = () => {
  const [showStakeModal, setShowStakeModal] = useState(false);
  const [showDetailsModal, setShowDetailsModal] = useState(false);
  const [selectedStake, setSelectedStake] = useState(null);
  const [stakeAmount, setStakeAmount] = useState('');
  const [selectedPlan, setSelectedPlan] = useState('Gold');
  const [stakedata, setStakedata] = useState({
    data: [],
    stake_income: 0,
    capping_income: 0,
    remain_income: 0,
  });
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const stakePlans = [
    { name: 'Basic',    minAmount: 100,  roi: '8%',  period: '30 days',  color: 'blue',   icon: 'bi-diamond', emoji: '🔵' },
    { name: 'Silver',   minAmount: 500,  roi: '12%', period: '60 days',  color: 'purple', icon: 'bi-gem',     emoji: '💎' },
    { name: 'Gold',     minAmount: 1000, roi: '18%', period: '90 days',  color: 'yellow', icon: 'bi-trophy',  emoji: '🏆' },
    { name: 'Platinum', minAmount: 5000, roi: '25%', period: '180 days', color: 'red',    icon: 'bi-star',    emoji: '⭐' },
  ];

  const analytics = [
    { title: 'Stake Income',   value: `$${stakedata?.stake_income || 0}`,   icon: 'bi-layers',    color: 'green' },
    { title: 'Capping Income', value: `$${stakedata?.capping_income || 0}`, icon: 'bi-shield',    color: 'blue' },
    { title: 'Total Income',   value: `$${stakedata?.capping_income || 0}`, icon: 'bi-bar-chart', color: 'yellow' },
    { title: 'Remain Income',  value: `$${stakedata?.remain_income || 0}`,  icon: 'bi-wallet2',   color: 'purple' },
  ];

  const openDetails = (stake) => { setSelectedStake(stake); setShowDetailsModal(true); };

  const submitStakeAmount = async () => {
    if (!stakeAmount) { toast.error('Please enter stake amount'); return; }
    setSubmitting(true);
    try {
      const response = await postApi('stake', {
        stake_amount: stakeAmount,
        wallet_type: 'available',
        plan: selectedPlan,
      });
      if (response?.data?.status) {
        toast.success(response.data.message || 'Stake Successful! 🎉');
        setShowStakeModal(false);
        setStakeAmount('');
        fetchActiveStakes();
      } else {
        toast.error(response?.data?.message || 'Failed to stake');
      }
    } catch (error) {
      toast.error('Something went wrong. Please try again!');
    } finally {
      setSubmitting(false);
    }
  };

  useEffect(() => { fetchActiveStakes(); }, []);

  const fetchActiveStakes = async () => {
    setLoading(true);
    try {
      const response = await postApi('stake-list');
      const stakeResult = response.data;
      if (stakeResult?.status === true) {
        setStakedata(stakeResult.data);
      } else {
        toast.error('Failed to load stakes');
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const selectedPlanData = stakePlans.find(p => p.name === selectedPlan) || stakePlans[2];
  const estimatedDaily = stakeAmount ? ((parseFloat(stakeAmount) * parseFloat(selectedPlanData.roi)) / 100 / 30).toFixed(2) : '0.00';

  return (
    <div className="page-wrapper">
      <ToastContainer position="top-right" autoClose={3000} theme="dark" />

      {/* Header */}
      <div className="page-header mb-4 d-flex justify-content-between align-items-center flex-wrap gap-3">
        <div>
          <div className="section-label">Earn Passive Income</div>
          <h2 className="page-title">Staking</h2>
          <p className="page-subtitle">Stake your assets and earn rewards every day</p>
        </div>
        <button className="btn btn-gradient" onClick={() => setShowStakeModal(true)}>
          <i className="bi bi-plus-circle me-2"></i>Stake Now
        </button>
      </div>

      {/* Analytics Cards */}
      <div className="row g-3 mb-4">
        {analytics.map((item, i) => (
          <div key={i} className="col-lg-3 col-md-6">
            <div className={`metric-card ${item.color}`}>
              <div className={`metric-icon ${item.color}`}>
                <i className={`bi ${item.icon}`}></i>
              </div>
              <div>
                <div className="metric-label">{item.title}</div>
                <div className="metric-value">
                  {loading ? <span className="skeleton d-inline-block" style={{ width: 80, height: 22 }}></span> : item.value}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Active Stakes Table */}
      <div className="p-card">
        <div className="d-flex justify-content-between align-items-center mb-4">
          <div>
            <p className="section-label mb-0">Portfolio</p>
            <h5 style={{ margin: 0, fontWeight: 700 }}>Active Stakes</h5>
          </div>
          <button className="btn btn-sm" onClick={fetchActiveStakes}
            style={{ background: 'rgba(0,255,149,0.1)', color: 'var(--page-accent)', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 10 }}>
            <i className="bi bi-arrow-clockwise me-1"></i>Refresh
          </button>
        </div>

        <div className="p-table-wrap">
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-success mb-3"></div>
              <p style={{ color: 'var(--text-secondary)' }}>Loading your stakes...</p>
            </div>
          ) : (stakedata?.data || []).length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon"><i className="bi bi-layers"></i></div>
              <h6>No Active Stakes</h6>
              <p>Start staking to earn daily rewards</p>
              <button className="btn btn-gradient mt-3" onClick={() => setShowStakeModal(true)}>
                <i className="bi bi-plus-circle me-2"></i>Stake Now
              </button>
            </div>
          ) : (
            <table className="p-table">
              <thead>
                <tr>
                  <th>#</th>
                  <th>Amount</th>
                  <th>Daily %</th>
                  <th>Daily Credit</th>
                  <th>Total Credit</th>
                  <th>Age</th>
                  <th>Date</th>
                  <th>Status</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {(stakedata?.data || []).map((stake, index) => (
                  <tr key={stake.id}>
                    <td className="dim">{index + 1}</td>
                    <td><span style={{ fontWeight: 700, color: 'var(--page-accent)' }}>${stake.stake_amount}</span></td>
                    <td>{stake.daily_reward_percentage}%</td>
                    <td className="mono">${stake.daily_credit_reward}</td>
                    <td className="mono" style={{ color: 'var(--page-blue)' }}>${stake.total_credit}</td>
                    <td className="dim">{stake.stake_age}</td>
                    <td className="dim" style={{ fontSize: '0.8rem' }}>{stake.date_time}</td>
                    <td>
                      <span className={`s-pill ${(stake.stake_status || 'active').toLowerCase()}`}>
                        {stake.stake_status || 'Active'}
                      </span>
                    </td>
                    <td>
                      <button className="btn btn-sm" onClick={() => openDetails(stake)}
                        style={{ background: 'rgba(255,255,255,0.05)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8 }}>
                        <i className="bi bi-eye me-1"></i>View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* STAKE MODAL */}
      {showStakeModal && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered modal-lg">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 pb-0 px-4 pt-4">
                <div>
                  <p className="section-label mb-0">New Position</p>
                  <h4 className="fw-800 mb-0" style={{ color: 'white' }}>Create Stake</h4>
                </div>
                <button className="btn-close btn-close-white" onClick={() => setShowStakeModal(false)}></button>
              </div>

              <div className="modal-body px-4 pb-4">
                {/* Plan Selection */}
                <p className="p-label mt-3 mb-2">Select Plan</p>
                <div className="row g-2 mb-4">
                  {stakePlans.map(plan => (
                    <div key={plan.name} className="col-6 col-md-3">
                      <button
                        onClick={() => setSelectedPlan(plan.name)}
                        style={{
                          width: '100%', padding: '1rem 0.5rem', borderRadius: 14,
                          background: selectedPlan === plan.name ? 'rgba(0,255,149,0.1)' : 'rgba(255,255,255,0.03)',
                          border: selectedPlan === plan.name ? '2px solid var(--page-accent)' : '1px solid rgba(255,255,255,0.08)',
                          color: 'white', cursor: 'pointer', transition: 'all 0.25s', textAlign: 'center',
                          boxShadow: selectedPlan === plan.name ? '0 0 20px rgba(0,255,149,0.2)' : 'none',
                        }}
                      >
                        <div style={{ fontSize: '1.5rem', marginBottom: 4 }}>{plan.emoji}</div>
                        <div style={{ fontWeight: 700, fontSize: '0.88rem' }}>{plan.name}</div>
                        <div style={{ color: 'var(--page-accent)', fontWeight: 800, fontSize: '1.1rem' }}>{plan.roi}</div>
                        <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: '0.75rem' }}>{plan.period}</div>
                        <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.72rem', marginTop: 2 }}>Min ${plan.minAmount}</div>
                      </button>
                    </div>
                  ))}
                </div>

                {/* Amount Input */}
                <p className="p-label mb-2">Enter Amount</p>
                <div className="input-group mb-3" style={{ background: 'rgba(255,255,255,0.04)', borderRadius: 14, overflow: 'hidden', border: '1px solid rgba(255,255,255,0.1)' }}>
                  <span style={{ background: 'transparent', border: 'none', color: 'var(--page-accent)', padding: '0 1.2rem', display: 'flex', alignItems: 'center', fontWeight: 700, fontSize: '1.1rem' }}>$</span>
                  <input
                    type="number"
                    style={{ background: 'transparent', border: 'none', color: 'white', fontSize: '1.3rem', padding: '0.9rem 0', fontFamily: 'JetBrains Mono, monospace', fontWeight: 700, flex: 1, outline: 'none' }}
                    placeholder="0.00"
                    value={stakeAmount}
                    onChange={e => setStakeAmount(e.target.value)}
                  />
                </div>

                {/* Preview */}
                {stakeAmount && (
                  <div style={{ background: 'rgba(0,255,149,0.06)', borderRadius: 14, padding: '1rem 1.2rem', border: '1px solid rgba(0,255,149,0.2)', marginBottom: '1rem' }}>
                    <div className="row g-2">
                      {[
                        { label: 'Plan', value: selectedPlanData.name },
                        { label: 'ROI',  value: selectedPlanData.roi },
                        { label: 'Est. Daily Reward', value: `$${estimatedDaily}` },
                        { label: 'Period', value: selectedPlanData.period },
                      ].map((r, i) => (
                        <div key={i} className="col-6">
                          <div className="info-row" style={{ padding: '0.35rem 0', borderBottomColor: 'rgba(0,255,149,0.1)' }}>
                            <span className="i-label" style={{ fontSize: '0.78rem' }}>{r.label}</span>
                            <span className="i-value" style={{ fontSize: '0.82rem', color: 'var(--page-accent)' }}>{r.value}</span>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Info */}
                <div style={{ background: 'rgba(247,201,72,0.06)', borderRadius: 12, padding: '0.85rem 1rem', border: '1px solid rgba(247,201,72,0.15)' }}>
                  {['Minimum stake $10 USDT', 'Rewards credited daily', 'Compound rewards for maximum returns'].map((t, i) => (
                    <p key={i} className="mb-1" style={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.82rem' }}>
                      <span style={{ color: 'var(--page-warning)', marginRight: 6 }}>●</span>{t}
                    </p>
                  ))}
                </div>
              </div>

              <div className="modal-footer border-0 px-4 pb-4 gap-2">
                <button className="btn flex-1" onClick={() => setShowStakeModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem 1.5rem', flex: 1 }}>
                  Cancel
                </button>
                <button className="btn btn-gradient" disabled={submitting || !stakeAmount}
                  onClick={submitStakeAmount}
                  style={{ flex: 2, borderRadius: 12, padding: '0.75rem', fontWeight: 700 }}>
                  {submitting ? <><span className="spinner-border spinner-border-sm me-2"></span>Staking...</> : <><i className="bi bi-layers me-2"></i>Confirm Stake</>}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* DETAILS MODAL */}
      {showDetailsModal && selectedStake && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.85)', backdropFilter: 'blur(4px)' }}>
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content" style={{ background: '#0a1628', border: '1px solid rgba(0,255,149,0.2)', borderRadius: 24 }}>
              <div className="modal-header border-0 px-4 pt-4 pb-0">
                <h5 className="fw-700 mb-0" style={{ color: 'white' }}>
                  <i className="bi bi-layers me-2" style={{ color: 'var(--page-accent)' }}></i>Stake Details
                </h5>
                <button className="btn-close btn-close-white" onClick={() => setShowDetailsModal(false)}></button>
              </div>
              <div className="modal-body px-4 py-3">
                <div className="text-center mb-4">
                  <div style={{ fontSize: '3rem', marginBottom: 8 }}>🏆</div>
                  <span className="s-pill active">{selectedStake.stake_status || 'Active'}</span>
                </div>
                {[
                  { label: 'Staked Amount',      value: `$${selectedStake.stake_amount}`,        color: 'var(--page-accent)' },
                  { label: 'Daily Reward %',      value: `${selectedStake.daily_reward_percentage}%`, color: 'var(--page-blue)' },
                  { label: 'Daily Credit',        value: `$${selectedStake.daily_credit_reward}`,  color: null },
                  { label: 'Total Credit So Far', value: `$${selectedStake.total_credit}`,          color: 'var(--page-accent)' },
                  { label: 'Stake Age',           value: selectedStake.stake_age,                   color: null },
                  { label: 'Last Updated',        value: selectedStake.last_update,                 color: null },
                ].map((d, i) => (
                  <div key={i} className="info-row">
                    <span className="i-label">{d.label}</span>
                    <span className="i-value" style={{ color: d.color || 'white', fontFamily: 'JetBrains Mono, monospace' }}>{d.value}</span>
                  </div>
                ))}
              </div>
              <div className="modal-footer border-0 px-4 pb-4">
                <button className="btn w-100" onClick={() => setShowDetailsModal(false)}
                  style={{ background: 'rgba(255,255,255,0.06)', color: 'white', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 12, padding: '0.75rem' }}>
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Stake;
